const mapImagePixelRatio = 2;
const rasterImageCache = new Map();
const styleImageLoadRegistry = new WeakMap();
const styleImageUrlRegistry = new WeakMap();
const mapAssetVersion =
  process.env.NODE_ENV === "development" ? `dev-${Date.now()}` : "2026-06-04-1";

const staticMapImageSpecs = [
  {
    imageId: "bus-stop-col-light",
    url: "/map-images/bus-stop-col.png",
  },
  {
    imageId: "bus-stop-col-dark",
    url: "/map-images/dark/bus-stop-col.png",
  },
  {
    imageId: "ferry-wharfs-col-light",
    url: "/map-images/ferry-wharfs-col.png",
  },
  {
    imageId: "ferry-wharfs-col-dark",
    url: "/map-images/dark/ferry-wharfs-col.png",
  },
  {
    imageId: "train-station-col-light",
    url: "/map-images/train-station-col.png",
  },
  {
    imageId: "train-station-col-dark",
    url: "/map-images/dark/train-station-col.png",
  },
  {
    imageId: "cable-car-stops-col-light",
    url: "/map-images/cable-car-stops-col.png",
  },
  {
    imageId: "cable-car-stops-col-dark",
    url: "/map-images/dark/cable-car-stops-col.png",
  },
  {
    imageId: "active-stop-bus-light",
    url: "/map-images/active-stop-bus.png",
  },
  {
    imageId: "active-stop-bus-dark",
    url: "/map-images/dark/active-stop-bus.png",
  },
  {
    imageId: "active-stop-ferry-light",
    url: "/map-images/active-stop-ferry.png",
  },
  {
    imageId: "active-stop-ferry-dark",
    url: "/map-images/dark/active-stop-ferry.png",
  },
  {
    imageId: "active-stop-train-light",
    url: "/map-images/active-stop-train.png",
  },
  {
    imageId: "active-stop-train-dark",
    url: "/map-images/dark/active-stop-train.png",
  },
  {
    imageId: "active-stop-cable-car-light",
    url: "/map-images/active-stop-cable-car.png",
  },
  {
    imageId: "active-stop-cable-car-dark",
    url: "/map-images/dark/active-stop-cable-car.png",
  },
  {
    imageId: "stop-destination-light",
    url: "/map-images/stop-destination.png",
  },
  {
    imageId: "stop-destination-dark",
    url: "/map-images/dark/stop-destination.png",
  },
  {
    imageId: "favourite-star-light",
    url: "/map-images/favourite.png",
  },
  {
    imageId: "favourite-star-dark",
    url: "/map-images/dark/favourite.png",
  },
  {
    imageId: "vehicle-position-marker",
    url: "/map-images/vehicle-position-marker.png",
  },
];

export const coreMapImageIds = new Set([
  ...staticMapImageSpecs.map(({ imageId }) => imageId),
  "search-position",
  "vehicle-arrow",
]);

function appendAssetVersion(url) {
  const separator = url.includes("?") ? "&" : "?";
  return `${url}${separator}v=${mapAssetVersion}`;
}

async function decodeRasterUrl(url) {
  const response = await fetch(appendAssetVersion(url));
  if (!response.ok) {
    throw new Error(`Failed to fetch map image asset: ${url}`);
  }

  const blob = await response.blob();
  const bitmap = await createImageBitmap(blob);

  try {
    const canvas = document.createElement("canvas");
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;

    const ctx = canvas.getContext("2d");
    if (!ctx) {
      throw new Error(`Unable to create canvas context for ${url}`);
    }

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(bitmap, 0, 0);

    return ctx.getImageData(0, 0, canvas.width, canvas.height);
  } finally {
    bitmap.close();
  }
}

function rasterUrlToImageData(url) {
  const key = appendAssetVersion(url);
  if (!rasterImageCache.has(key)) {
    const promise = decodeRasterUrl(url).catch((error) => {
      rasterImageCache.delete(key);
      throw error;
    });
    rasterImageCache.set(key, promise);
  }
  return rasterImageCache.get(key);
}

async function loadStyleImage(mapInstance, imageId, url) {
  if (!mapInstance) {
    return;
  }

  let imagePromises = styleImageLoadRegistry.get(mapInstance);
  if (!imagePromises) {
    imagePromises = new Map();
    styleImageLoadRegistry.set(mapInstance, imagePromises);
  }

  let imageUrls = styleImageUrlRegistry.get(mapInstance);
  if (!imageUrls) {
    imageUrls = new Map();
    styleImageUrlRegistry.set(mapInstance, imageUrls);
  }

  const previousUrl = imageUrls.get(imageId);
  if (mapInstance.hasImage(imageId) && previousUrl === url) {
    return;
  }

  if (imagePromises.has(imageId)) {
    await imagePromises.get(imageId);
    return;
  }

  const loadPromise = (async () => {
    const imageData = await rasterUrlToImageData(url);

    if (mapInstance.hasImage(imageId)) {
      if (typeof mapInstance.updateImage === "function") {
        mapInstance.updateImage(imageId, imageData);
      } else {
        mapInstance.removeImage(imageId);
        mapInstance.addImage(imageId, imageData, {
          pixelRatio: mapImagePixelRatio,
        });
      }
    } else {
      mapInstance.addImage(imageId, imageData, {
        pixelRatio: mapImagePixelRatio,
      });
    }

    imageUrls.set(imageId, url);
  })();

  imagePromises.set(imageId, loadPromise);

  try {
    await loadPromise;
  } finally {
    if (imagePromises.get(imageId) === loadPromise) {
      imagePromises.delete(imageId);
    }
  }
}

export async function ensureCoreMapImages(
  mapInstance,
  { isDark = false, preferSatellite = false } = {},
) {
  const useDarkAsset = isDark || preferSatellite;
  const searchPositionUrl = useDarkAsset
    ? "/map-images/dark/search-position.png"
    : "/map-images/search-position.png";
  const vehicleArrowUrl = useDarkAsset
    ? "/map-images/dark/vehicle-arrow.png"
    : "/map-images/vehicle-arrow.png";

  const imageSpecs = [
    ...staticMapImageSpecs,
    { imageId: "search-position", url: searchPositionUrl },
    { imageId: "vehicle-arrow", url: vehicleArrowUrl },
  ];

  await Promise.all(
    imageSpecs.map(({ imageId, url }) =>
      loadStyleImage(mapInstance, imageId, url),
    ),
  );
}
